import React, { ReactNode, useEffect } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { LoadingSpinner, Modal } from "@mapmycustomers/ui";

import emailSent from "@app/assets/email/email-sent.svg";
import HelpText from "@app/component/typography/HelpText";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import {
  isEmailQuotaLoading,
  isOrgReachEmailDayLimit,
  isOutOfEmailSendingHours,
} from "@app/store/email";
import { fetchEmailQuota, showEmailFeatures } from "@app/store/email/actions";
import { RootState } from "@app/store/rootReducer";

import EmailPeriod from "./EmailPeriod";
import styles from "./EmailSentModal.module.scss";
import EmailSummaryInfo from "./EmailSummaryInfo";
import messages from "./messages";

interface EmailSentModalProps {
  onHide: () => void;
}

const EmailSentModal = ({
  emailQuotaLoading,
  isOrgReachEmailDayLimit,
  isOutOfEmailSendingHours,
  onEmailFeaturesShow,
  onEmailQuotaFetch,
  onHide,
}: EmailSentModalProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();

  useEffect(() => {
    onEmailQuotaFetch();
  }, [onEmailQuotaFetch]);

  return (
    <Modal
      className={styles.modal}
      footer={null}
      onCancel={onHide}
      open
      title={emailQuotaLoading ? <LoadingSpinner mini /> : null}
      width="27rem"
    >
      <div className={styles.container}>
        <img alt="email-sent" className={styles.image} src={emailSent} />
        <div className={styles.title}>
          {isOrgReachEmailDayLimit || isOutOfEmailSendingHours
            ? intl.formatMessage(messages.emailIsQueued)
            : intl.formatMessage(messages.emailIsSent)}
        </div>
        <EmailPeriod onSettings={onHide} showIcon={isOutOfEmailSendingHours} />
        <div className={styles.subTitle}>
          {isOrgReachEmailDayLimit
            ? intl.formatMessage(messages.reachSendLimit)
            : isOutOfEmailSendingHours
              ? intl.formatMessage(messages.outSideSchedule, {
                  a: (text: ReactNode) => (
                    <Link
                      onClick={onHide}
                      to={`${Path.SETTINGS}/${SettingPath.PERSONAL_EMAIL_PREFERENCES}`}
                    >
                      {text}
                    </Link>
                  ),
                })
              : intl.formatMessage(messages.emailIsSentDescription)}
        </div>
        <EmailSummaryInfo />
        <div className={styles.bottom}>
          <HelpText
            onClick={onEmailFeaturesShow}
            text={intl.formatMessage(messages.emailIsSentAboutEmails)}
          />
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  emailQuotaLoading: isEmailQuotaLoading(state),
  isOrgReachEmailDayLimit: isOrgReachEmailDayLimit(state),
  isOutOfEmailSendingHours: isOutOfEmailSendingHours(state),
});

const mapDispatchToProps = {
  onEmailFeaturesShow: showEmailFeatures,
  onEmailQuotaFetch: fetchEmailQuota.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailSentModal);
